import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router';
import { Spinner, Title2, Text, makeStyles, tokens } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { ProtectedRoute } from '@/auth';
import { useTelemetry } from '@/telemetry/useTelemetry';
import { AssessmentProvider } from '@/features/assessment/AssessmentProvider';

const LandingPage = lazy(() => import('@/features/landing/LandingPage').then((m) => ({ default: m.LandingPage })));
const PingPage = lazy(() => import('@/features/ping/PingPage').then((m) => ({ default: m.PingPage })));
const EntryPage = lazy(() => import('@/features/assessment/EntryPage').then((m) => ({ default: m.EntryPage })));
const PartnerDetailsPage = lazy(() => import('@/features/assessment/PartnerDetailsPage').then((m) => ({ default: m.PartnerDetailsPage })));
const OverviewPage = lazy(() => import('@/features/assessment/OverviewPage').then((m) => ({ default: m.OverviewPage })));
const QuestionPage = lazy(() => import('@/features/assessment/QuestionPage').then((m) => ({ default: m.QuestionPage })));
const ReviewPage = lazy(() => import('@/features/assessment/ReviewPage').then((m) => ({ default: m.ReviewPage })));
const ResultsPage = lazy(() => import('@/features/assessment/ResultsPage').then((m) => ({ default: m.ResultsPage })));
const FacilitatorReportPage = lazy(() =>
  import('@/features/facilitator/FacilitatorReportPage').then((m) => ({ default: m.FacilitatorReportPage })),
);

const useStyles = makeStyles({
  notFound: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    gap: tokens.spacingVerticalL,
  },
});

function LoadingFallback(): React.ReactElement {
  const { t } = useTranslation('aita');
  return <Spinner label={t('entry.loading_page', 'Loading...')} style={{ marginTop: '20vh' }} />;
}

/**
 * One-shot navigation to the post-login path restored from the OAuth state
 * round-trip (plan §4.7). Renders <Navigate> exactly once, then becomes inert
 * so subsequent in-app navigation isn't hijacked. Consumption is ALSO reported
 * upward (App holds it above the ErrorBoundary): the local flag dies with this
 * component on an error-boundary remount, and the redirect must not replay.
 */
function InitialRedirect({
  path,
  onConsumed,
}: {
  path: string;
  onConsumed?: () => void;
}): React.ReactElement | null {
  const [consumed, setConsumed] = useState(false);

  useEffect(() => {
    setConsumed(true);
    onConsumed?.();
  }, [onConsumed]);

  if (consumed) return null;
  return <Navigate to={path} replace />;
}

function NotFoundPage(): React.ReactElement {
  const styles = useStyles();
  const { t } = useTranslation('aita');
  const { trackPageView } = useTelemetry();

  React.useEffect(() => {
    trackPageView('aita.notfound');
  }, [trackPageView]);

  return (
    <div className={styles.notFound}>
      <Title2>{t('notfound.title', 'Page not found')}</Title2>
      <Text>{t('notfound.description', 'The page you are looking for does not exist.')}</Text>
    </div>
  );
}

/** Protected layout for the /app subtree — one AssessmentProvider for all screens. */
function AssessmentLayout(): React.ReactElement {
  return (
    <ProtectedRoute>
      <AssessmentProvider>
        <Outlet />
      </AssessmentProvider>
    </ProtectedRoute>
  );
}

interface AppRouterProps {
  /** Router path to restore after a login redirect round-trip, if any */
  redirectPath?: string | null;
  /** Reports the one-shot redirect as consumed (held above the ErrorBoundary) */
  onRedirectConsumed?: () => void;
}

export function AppRouter({ redirectPath, onRedirectConsumed }: AppRouterProps = {}): React.ReactElement {
  return (
    <Suspense fallback={<LoadingFallback />}>
      {redirectPath && <InitialRedirect path={redirectPath} onConsumed={onRedirectConsumed} />}
      <Routes>
        <Route path="/" element={<LandingPage />} />
        {/* Facilitator report (lift-and-shift from Power BI). Protected but NOT wrapped in
            AssessmentProvider -- facilitators view partner results, they do not take the
            assessment here. Served at /AITA/facilitator via APP_BASENAME. The page itself
            gates on the aitaFacilitatorReport_783419 flag and the API's facilitator role. */}
        <Route
          path="/facilitator"
          element={
            <ProtectedRoute>
              <FacilitatorReportPage />
            </ProtectedRoute>
          }
        />
        <Route path="/app" element={<AssessmentLayout />}>
          <Route index element={<EntryPage />} />
          <Route path="details" element={<PartnerDetailsPage />} />
          <Route path="overview" element={<OverviewPage />} />
          <Route path="question/:n" element={<QuestionPage />} />
          <Route path="review" element={<ReviewPage />} />
          <Route path="results/:id" element={<ResultsPage />} />
          <Route path="ping" element={<PingPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
